import type { DocWatchingRow } from '~/types/doc-watching';
import type { EscalatedDocRow } from '~/types/escalated-docs';
import type {
  NotificationCountResponse,
  NotificationDeleteResponse,
  NotificationListParams,
  NotificationListResponse,
  NotificationMarkReadResponse,
  NotificationReadAllResponse,
  NotificationRow,
  NotificationSourceFeature,
} from '~/types/notifications';
import type { RejectedDocRow } from '~/types/rejected-docs';
import type { SlotSlaRow } from '~/types/slot-sla';
import type { WorkflowSlaRow } from '~/types/workflow-sla';
import type { PendingRow } from '~/types/workflow-cycles';

type ApiPage<T> = {
  data?: {
    items?: T[];
    pagination?: {
      total?: number;
      currentPage?: number;
      perPage?: number;
      totalPages?: number;
    };
  };
};

type YourWorkSource =
  | 'private_inbox'
  | 'distribution_inbox'
  | 'pending_signature'
  | 'rejected_docs'
  | 'doc_watching'
  | 'escalation'
  | 'workflow_sla'
  | 'slot_sla';

type YourWorkRowsResult = {
  rows: NotificationRow[];
  total: number;
};

const LEGACY_ID_PREFIX = {
  private_inbox: 'private-inbox:',
  distribution_inbox: 'distribution-inbox:',
  pending_signature: 'pending-signature:',
  rejected_docs: 'rejected-docs:',
  doc_watching: 'doc-watching:',
  escalation: 'escalated-docs:',
  workflow_sla: 'workflow-sla:',
  slot_sla: 'slot-sla:',
} as const;

const CORE_SUPPORTED_SOURCES = new Set<NotificationSourceFeature>([
  'workflow_sla',
  'slot_sla',
  'escalation',
  'private_inbox',
  'distribution_inbox',
  'pending_signature',
  'signed_document_activity',
  'project_form_document',
]);

const LEGACY_FALLBACK_SOURCES = new Set<YourWorkSource>([
  'rejected_docs',
  'doc_watching',
]);

const YOUR_WORK_SOURCES = Object.keys(LEGACY_ID_PREFIX) as YourWorkSource[];
const isYourWorkSource = (
  source?: NotificationSourceFeature | null,
): source is YourWorkSource =>
  !!source && YOUR_WORK_SOURCES.includes(source as YourWorkSource);

const localizedText = (value: unknown): string | null => {
  let raw = value;

  for (let i = 0; i < 2; i++) {
    if (typeof raw !== 'string') break;

    try {
      raw = JSON.parse(raw);
    } catch {
      break;
    }
  }

  if (Array.isArray(raw)) {
    const first = raw.find((item) => item !== null && item !== undefined);
    return localizedText(first);
  }

  if (raw && typeof raw === 'object') {
    const obj = raw as Record<string, unknown>;
    return localizedText(
      obj.subject ?? obj.value ?? obj.en ?? obj.ar ?? Object.values(obj)[0],
    );
  }

  if (raw === null || raw === undefined) return null;

  const text = String(raw).trim();
  return text === '' ? null : text;
};

const typeKey = (item: any): string =>
  String(item?.type?.en ?? item?.type ?? '')
    .trim()
    .toLowerCase();

const numericId = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (
      typeof value === 'string' &&
      value.trim() !== '' &&
      !Number.isNaN(Number(value))
    ) {
      return Number(value);
    }
  }

  return null;
};

const buildDistributionQuery = (
  projectName?: string | null,
  wbsName?: string | null,
): string => {
  const params = new URLSearchParams();
  if (projectName) params.set('name', projectName);
  if (wbsName) params.set('wbs', wbsName);

  const query = params.toString();
  return query ? `?${query}` : '';
};

const documentWorkflowLink = (
  projectId?: number | null,
  formId?: number | null,
  submissionId?: number | null,
  cycleId?: number | null,
): string => {
  if (!projectId || !formId || !submissionId) {
    return '/your-work';
  }

  const query = new URLSearchParams({
    submissionId: String(submissionId),
    tab: 'workflow',
  });

  if (cycleId) {
    query.set('cycle', String(cycleId));
  }

  return `/projects/${projectId}/forms/${formId}/submission/view?${query.toString()}`;
};

const sourceFallbackSubject = (source: NotificationSourceFeature): string => {
  switch (source) {
    case 'private_inbox':
      return 'Private inbox document';
    case 'distribution_inbox':
      return 'Distribution inbox document';
    case 'pending_signature':
      return 'Pending signature';
    case 'rejected_docs':
      return 'Rejected document';
    case 'doc_watching':
      return 'Watched document';
    case 'escalation':
      return 'Escalated document';
    case 'workflow_sla':
      return 'Workflow SLA breached';
    case 'slot_sla':
      return 'Slot SLA breached';
    default:
      return 'Notification';
  }
};

const formUpdateLink = (item: any, fallback: string): string => {
  const formId = numericId(
    item?.templateFormVersionId,
    item?.template_form_version_id,
    item?.templates_form_version_id,
    item?.typeId,
    item?.type_id,
  );

  if (formId === null) {
    return fallback;
  }

  const mainId = numericId(
    item?.mainFormId,
    item?.mainId,
    item?.main_template_form_id,
  );
  const query = mainId === null
    ? ''
    : `?${new URLSearchParams({ mainId: String(mainId) }).toString()}`;

  return `/forms/${formId}/update${query}`;
};

const privateInboxLink = (item: any): string => {
  const key = typeKey(item);
  const typeId = numericId(item?.typeId, item?.type_id);
  const projectId = numericId(
    item?.project?.id,
    item?.projectId,
    item?.project_id,
  );
  const wbsId = numericId(item?.project?.wbs?.id, item?.wbsId, item?.w_b_s_id);

  if (key === 'company' && typeId !== null) {
    return `/enterprise-directory/${typeId}/update`;
  }
  if (key === 'project') {
    return wbsId !== null && (projectId !== null || typeId !== null)
      ? `/wbs/${wbsId}/projects/${projectId ?? typeId}/update`
      : '/your-work/inbox';
  }
  if (key === 'submission' && projectId !== null && typeId !== null) {
    const mainTemplateFormId = numericId(
      item?.mainTemplateFormId,
      item?.main_template_form_id,
    );
    const formVersion = numericId(item?.formVersion, item?.form_version);
    if (mainTemplateFormId !== null && formVersion !== null) {
      return `/projects/${projectId}/forms/${mainTemplateFormId}/submission/update?version=${formVersion}&submissionId=${typeId}`;
    }
  }
  if (key === 'workflow' && projectId !== null && typeId !== null) {
    return `/projects/${projectId}/workflow/${typeId}/update`;
  }
  if (
    (key === 'distribution' || key === 'distribution list') &&
    projectId !== null &&
    typeId !== null
  ) {
    const projectName = localizedText(item?.project?.name ?? item?.projectName);
    const wbsName = localizedText(item?.project?.wbs?.title ?? item?.wbsName);
    return `/projects/${projectId}/distribution-group/${typeId}/update${buildDistributionQuery(projectName, wbsName)}`;
  }
  if (key === 'form' && typeId !== null) {
    return formUpdateLink(item, '/your-work/inbox');
  }

  return '/your-work/inbox';
};

const distributionInboxLink = (item: any): string => {
  const key = typeKey(item);
  const typeId = numericId(item?.typeId, item?.type_id);
  const projectId = numericId(
    item?.project?.id,
    item?.projectId,
    item?.project_id,
  );
  const wbsId = numericId(item?.project?.wbs?.id, item?.wbsId, item?.w_b_s_id);

  if (key === 'company' && typeId !== null) {
    return `/enterprise-directory/${typeId}/update`;
  }
  if (key === 'project') {
    return wbsId !== null && projectId !== null
      ? `/wbs/${wbsId}/projects/${projectId}/update`
      : '/your-work/distribution';
  }
  if (key === 'submission' && projectId !== null && typeId !== null) {
    const mainTemplateFormId = numericId(
      item?.mainTemplateFormId,
      item?.main_template_form_id,
    );
    const formVersion = numericId(item?.formVersion, item?.form_version);
    if (mainTemplateFormId !== null && formVersion !== null) {
      return `/projects/${projectId}/forms/${mainTemplateFormId}/submission/update?version=${formVersion}&submissionId=${typeId}`;
    }
  }
  if (key === 'workflow' && projectId !== null && typeId !== null) {
    return `/projects/${projectId}/workflow/${typeId}/update`;
  }
  if (
    (key === 'distribution' || key === 'distribution list') &&
    projectId !== null &&
    typeId !== null
  ) {
    const projectName = localizedText(item?.project?.name ?? item?.project);
    const wbsName = localizedText(item?.project?.wbs?.title ?? item?.wbsName);
    return `/projects/${projectId}/distribution-group/${typeId}/update${buildDistributionQuery(projectName, wbsName)}`;
  }
  if (key === 'form' && typeId !== null) {
    return formUpdateLink(item, '/your-work/distribution');
  }

  return '/your-work/distribution';
};

const legacyRow = (
  source: YourWorkSource,
  item: any,
): NotificationRow | null => {
  const id = numericId(item?.id);
  if (id === null) return null;

  const isRead = String(item?.status ?? '').toLowerCase() === 'read';
  const subject =
    localizedText(item?.subject) ??
    localizedText(item?.type) ??
    sourceFallbackSubject(source);

  return {
    id: `${LEGACY_ID_PREFIX[source]}${id}`,
    sourceFeature: source,
    sourceEntityId: id,
    subject,
    message: localizedText(item?.message),
    link:
      source === 'private_inbox'
        ? privateInboxLink(item)
        : distributionInboxLink(item),
    isRead,
    readAt: isRead
      ? (item?.updatedAt ??
        item?.updated_at ??
        item?.createdAt ??
        item?.created_at ??
        null)
      : null,
    createdAt: item?.createdAt ?? item?.created_at ?? null,
  };
};

const pendingSignatureRow = (item: PendingRow): NotificationRow | null => {
  const id = numericId(item?.signature_id, item?.id);
  if (id === null) return null;

  const projectId = numericId(item?.project_id);
  const formId = numericId(item?.form_id);
  const documentId = numericId(item?.document_id);
  const actionBy =
    typeof item?.action_by_display === 'string'
      ? item.action_by_display
      : localizedText(item?.action_by_display?.method);

  return {
    id: `${LEGACY_ID_PREFIX.pending_signature}${id}`,
    sourceFeature: 'pending_signature',
    sourceEntityId: id,
    subject:
      localizedText(item?.subject) ??
      sourceFallbackSubject('pending_signature'),
    message: actionBy ? `Action by: ${actionBy}` : null,
    link:
      projectId && formId && documentId
        ? documentWorkflowLink(
            projectId,
            formId,
            documentId,
            numericId(item?.cycle_id),
          )
        : '/your-work/pending-signatures',
    isRead: false,
    readAt: null,
    createdAt: null,
  };
};

const rejectedDocRow = (item: RejectedDocRow): NotificationRow | null => {
  const id = numericId(item?.id);
  if (id === null) return null;

  const rejectedBy = localizedText(item?.rejected_by_name);
  const comment = localizedText(item?.rejection_comment);

  return {
    id: `${LEGACY_ID_PREFIX.rejected_docs}${id}`,
    sourceFeature: 'rejected_docs',
    sourceEntityId: id,
    subject:
      localizedText(item?.subject) ?? sourceFallbackSubject('rejected_docs'),
    message: comment ?? (rejectedBy ? `Rejected by ${rejectedBy}` : null),
    link: documentWorkflowLink(
      numericId(item?.project_id),
      numericId(item?.form_id),
      numericId(item?.document_id),
      numericId(item?.cycle_id),
    ),
    isRead: item?.read_at !== null,
    readAt: item?.read_at ?? null,
    createdAt: item?.rejected_at ?? null,
  };
};

const docWatchingRow = (item: DocWatchingRow): NotificationRow | null => {
  const id = numericId(item?.cycleId);
  if (id === null) return null;

  const workflowTitle = localizedText(item?.workflowTitle);
  const projectName = localizedText(item?.project?.name);

  return {
    id: `${LEGACY_ID_PREFIX.doc_watching}${id}`,
    sourceFeature: 'doc_watching',
    sourceEntityId: id,
    subject:
      localizedText(item?.subject) ?? sourceFallbackSubject('doc_watching'),
    message: [workflowTitle, projectName].filter(Boolean).join(' - ') || null,
    link: documentWorkflowLink(
      numericId(item?.project?.id),
      numericId(item?.formId),
      numericId(item?.documentId),
      id,
    ),
    isRead: false,
    readAt: null,
    createdAt: item?.lastActivityAt ?? item?.submittedAt ?? null,
  };
};

const escalationReasonText = (
  reason: EscalatedDocRow['escalation_reason'],
): string | null => {
  if (!reason?.type) return null;

  if (reason.type === 'before_sla' || reason.type === 'after_sla') {
    return `${reason.type.replace('_', ' ')} ${reason.value ?? ''} ${reason.unit ?? ''}`.trim();
  }

  return reason.type.replace('_', ' ');
};

const escalatedDocRow = (item: EscalatedDocRow): NotificationRow | null => {
  const id = numericId(item?.id);
  if (id === null) return null;

  const reason = escalationReasonText(item?.escalation_reason);
  const assignee = localizedText(item?.original_assignee_name);

  return {
    id: `${LEGACY_ID_PREFIX.escalation}${id}`,
    sourceFeature: 'escalation',
    sourceEntityId: id,
    subject:
      localizedText(item?.subject) ?? sourceFallbackSubject('escalation'),
    message: reason ?? (assignee ? `Original assignee: ${assignee}` : null),
    link: documentWorkflowLink(
      numericId(item?.project_id),
      numericId(item?.form_id),
      numericId(item?.document_id),
      numericId(item?.cycle_id),
    ),
    isRead: item?.read_at !== null,
    readAt: item?.read_at ?? null,
    createdAt: item?.escalated_at ?? item?.trigger_instant ?? null,
  };
};

const workflowSlaRow = (item: WorkflowSlaRow): NotificationRow | null => {
  const id = numericId(item?.documentId);
  if (id === null) return null;

  const workflowTitle = localizedText(item?.workflowTitle);

  return {
    id: `${LEGACY_ID_PREFIX.workflow_sla}${id}`,
    sourceFeature: 'workflow_sla',
    sourceEntityId: id,
    subject: `Workflow SLA breached on document #${id}`,
    message: workflowTitle ?? item?.slaLimitLabel ?? null,
    link: documentWorkflowLink(
      numericId(item?.project?.id),
      numericId(item?.formId),
      id,
      numericId(item?.workflowInstanceId ?? item?.firstCycleId),
    ),
    isRead: !!item?.isRead,
    readAt: item?.readAt ?? null,
    createdAt: item?.firstCycleStartedAt ?? null,
  };
};

const slotSlaRow = (item: SlotSlaRow): NotificationRow | null => {
  const id = numericId(item?.slotInstanceId);
  if (id === null) return null;

  const slotTitle = localizedText(item?.slotTitle);

  return {
    id: `${LEGACY_ID_PREFIX.slot_sla}${id}`,
    sourceFeature: 'slot_sla',
    sourceEntityId: id,
    subject: `Slot SLA breached on document #${item?.documentId}`,
    message: slotTitle ?? item?.slaLimitLabel ?? null,
    link: documentWorkflowLink(
      numericId(item?.project?.id),
      numericId(item?.formId),
      numericId(item?.documentId),
      numericId(item?.cycleId),
    ),
    isRead: !!item?.isRead,
    readAt: item?.readAt ?? null,
    createdAt: item?.slotActivatedAt ?? null,
  };
};

const yourWorkMarkReadTarget = (
  id: string,
): { source: YourWorkSource; id: number } | null => {
  for (const source of YOUR_WORK_SOURCES) {
    const prefix = LEGACY_ID_PREFIX[source];
    if (!id.startsWith(prefix)) continue;

    const rawId = id.slice(prefix.length);
    if (rawId === '' || Number.isNaN(Number(rawId))) return null;

    return { source, id: Number(rawId) };
  }

  return null;
};

const isMarkableYourWorkSource = (source: YourWorkSource): boolean =>
  source !== 'pending_signature' && source !== 'doc_watching';

const emptyNotificationsPage = (
  page: number,
  perPage: number,
): NotificationListResponse => ({
  data: {
    items: [],
    pagination: {
      total: 0,
      currentPage: page,
      perPage,
      totalPages: 0,
    },
  },
});

const dateValue = (iso: string | null): number => {
  if (!iso) return 0;

  const value = new Date(iso).getTime();
  return Number.isFinite(value) ? value : 0;
};

const readParams = (
  read?: NotificationListParams['read'],
): Record<string, string> => (read && read !== 'all' ? { read } : {});

const inboxBackendParams = (
  page: number,
  perPage: number,
  params?: NotificationListParams,
): Record<string, string | number> => {
  const query: Record<string, string | number> = { page, perPage };

  if (params?.read && params.read !== 'all') {
    query.status = params.read;
  }
  if (params?.dateFrom) {
    query.createdAtStart = params.dateFrom;
  }
  if (params?.dateTo) {
    query.createdAtEnd = params.dateTo;
  }

  return query;
};

const filterRowsByDate = (
  rows: NotificationRow[],
  params?: NotificationListParams,
): NotificationRow[] => {
  const from = params?.dateFrom ? new Date(params.dateFrom).getTime() : null;
  const to = params?.dateTo ? new Date(params.dateTo).getTime() : null;

  if (!Number.isFinite(from) && !Number.isFinite(to)) return rows;

  return rows.filter((row) => {
    const value = dateValue(row.createdAt);
    if (from !== null && Number.isFinite(from) && value < from) return false;
    if (to !== null && Number.isFinite(to) && value > to) return false;
    return true;
  });
};

const fetchPrivateOrDistributionRows = async (
  source: Extract<YourWorkSource, 'private_inbox' | 'distribution_inbox'>,
  page: number,
  perPage: number,
  params?: NotificationListParams,
): Promise<YourWorkRowsResult> => {
  const resp = await $fetch<ApiPage<any>>(
    source === 'private_inbox'
      ? '/api/private-inbox/my-private'
      : '/api/distribution-inbox/private-message',
    {
      params: inboxBackendParams(page, perPage, params),
      credentials: 'include',
    },
  );

  const rows = (resp?.data?.items ?? [])
    .map((item) => legacyRow(source, item))
    .filter((row): row is NotificationRow => row !== null);

  return {
    rows,
    total: resp?.data?.pagination?.total ?? rows.length,
  };
};

const fetchYourWorkRows = async (
  source: YourWorkSource,
  page: number,
  perPage: number,
  params?: NotificationListParams,
): Promise<YourWorkRowsResult> => {
  const read = params?.read;

  if (source === 'private_inbox' || source === 'distribution_inbox') {
    return fetchPrivateOrDistributionRows(source, page, perPage, params);
  }

  if (source === 'pending_signature') {
    if (read === 'read') return { rows: [], total: 0 };

    const resp = await $fetch<ApiPage<PendingRow>>(
      '/api/me/pending-signatures',
      {
        params: { page, limit: perPage },
        credentials: 'include',
      },
    );
    const rows = (resp?.data?.items ?? [])
      .map(pendingSignatureRow)
      .filter((row): row is NotificationRow => row !== null);

    return {
      rows,
      total: resp?.data?.pagination?.total ?? rows.length,
    };
  }

  if (source === 'rejected_docs') {
    const resp = await $fetch<ApiPage<RejectedDocRow>>(
      '/api/me/rejected-docs',
      {
        params: { page, limit: perPage, ...readParams(read) },
        credentials: 'include',
      },
    );
    const rows = (resp?.data?.items ?? [])
      .map(rejectedDocRow)
      .filter((row): row is NotificationRow => row !== null);

    return {
      rows,
      total: resp?.data?.pagination?.total ?? rows.length,
    };
  }

  if (source === 'doc_watching') {
    if (read === 'read') return { rows: [], total: 0 };

    const resp = await $fetch<ApiPage<DocWatchingRow>>('/api/me/doc-watching', {
      params: { page, limit: perPage, sort: 'last_activity_at_desc' },
      credentials: 'include',
    });
    const rows = (resp?.data?.items ?? [])
      .map(docWatchingRow)
      .filter((row): row is NotificationRow => row !== null);

    return {
      rows,
      total: resp?.data?.pagination?.total ?? rows.length,
    };
  }

  if (source === 'escalation') {
    const resp = await $fetch<ApiPage<EscalatedDocRow>>(
      '/api/me/escalated-docs',
      {
        params: { page, limit: perPage, ...readParams(read) },
        credentials: 'include',
      },
    );
    const rows = (resp?.data?.items ?? [])
      .map(escalatedDocRow)
      .filter((row): row is NotificationRow => row !== null);

    return {
      rows,
      total: resp?.data?.pagination?.total ?? rows.length,
    };
  }

  if (source === 'workflow_sla') {
    const resp = await $fetch<ApiPage<WorkflowSlaRow>>('/api/me/workflow-sla', {
      params: { page, limit: perPage, ...readParams(read) },
      credentials: 'include',
    });
    const rows = (resp?.data?.items ?? [])
      .map(workflowSlaRow)
      .filter((row): row is NotificationRow => row !== null);

    return {
      rows,
      total: resp?.data?.pagination?.total ?? rows.length,
    };
  }

  const resp = await $fetch<ApiPage<SlotSlaRow>>('/api/me/slot-sla', {
    params: { page, limit: perPage, ...readParams(read) },
    credentials: 'include',
  });
  const rows = (resp?.data?.items ?? [])
    .map(slotSlaRow)
    .filter((row): row is NotificationRow => row !== null);

  return {
    rows,
    total: resp?.data?.pagination?.total ?? rows.length,
  };
};

const countNumber = (...values: unknown[]): number => {
  const n = numericId(...values);
  return n ?? 0;
};

const fetchCoreUnreadCount = async (): Promise<number> => {
  const resp = await $fetch<NotificationCountResponse>(
    '/api/me/notifications/count',
    {
      credentials: 'include',
    },
  );

  return countNumber(resp?.data?.unRead, resp?.unRead);
};

const markYourWorkRead = async (
  source: YourWorkSource,
  id: number,
): Promise<boolean> => {
  if (source === 'private_inbox') {
    await $fetch('/api/private-inbox/mark-read', {
      method: 'PUT',
      body: { messagesIds: [id] },
      credentials: 'include',
    });
    return true;
  }

  if (source === 'distribution_inbox') {
    await $fetch('/api/distribution-inbox/mark-read', {
      method: 'PUT',
      body: { messagesIds: [id] },
      credentials: 'include',
    });
    return true;
  }

  if (source === 'rejected_docs') {
    await $fetch(`/api/me/rejected-docs/${id}/read`, {
      method: 'POST',
      credentials: 'include',
    });
    return true;
  }

  if (source === 'escalation') {
    await $fetch(`/api/me/escalated-docs/${id}/read`, {
      method: 'POST',
      credentials: 'include',
    });
    return true;
  }

  if (source === 'workflow_sla') {
    await $fetch(`/api/me/workflow-sla/${id}/read`, {
      method: 'POST',
      credentials: 'include',
    });
    return true;
  }

  if (source === 'slot_sla') {
    await $fetch(`/api/me/slot-sla/${id}/read`, {
      method: 'POST',
      credentials: 'include',
    });
    return true;
  }

  return false;
};

let notificationCountRequest: Promise<number> | null = null;

export const useNotifications = () => {
  const count = useState<number>('notificationsUnreadCount', () => 0);

  const list = async (
    params?: NotificationListParams,
  ): Promise<NotificationListResponse> => {
    const requestedSource = params?.sourceFeature;
    const perPage = params?.limit ?? 25;
    const page = params?.page ?? 1;

    if (
      requestedSource &&
      isYourWorkSource(requestedSource) &&
      LEGACY_FALLBACK_SOURCES.has(requestedSource)
    ) {
      const { rows, total } = await fetchYourWorkRows(
        requestedSource,
        page,
        perPage,
        params,
      );

      return {
        data: {
          items: filterRowsByDate(rows, params).slice(0, perPage),
          pagination: {
            total,
            currentPage: page,
            perPage,
            totalPages: Math.ceil(total / perPage),
          },
        },
      };
    }

    const cleaned = Object.fromEntries(
      Object.entries(params ?? {}).filter(
        ([key, v]) =>
          key !== 'sourceFeature' && v !== undefined && v !== null && v !== '',
      ),
    );
    const coreParams: Record<string, unknown> = { ...cleaned };

    if (requestedSource && CORE_SUPPORTED_SOURCES.has(requestedSource)) {
      coreParams.sourceFeature = requestedSource;
    }

    if (requestedSource && !CORE_SUPPORTED_SOURCES.has(requestedSource)) {
      return emptyNotificationsPage(page, perPage);
    }

    return await $fetch<NotificationListResponse>('/api/me/notifications', {
      params: coreParams,
      credentials: 'include',
    });
  };

  const fetchCount = async (options?: { force?: boolean }): Promise<number> => {
    if (notificationCountRequest) {
      return notificationCountRequest;
    }

    notificationCountRequest = (async () => {
      void options;

      const n = await fetchCoreUnreadCount();
      count.value = n;
      return n;
    })().catch(() => count.value);

    try {
      return await notificationCountRequest;
    } finally {
      notificationCountRequest = null;
    }
  };

  const markRead = async (
    id: string,
  ): Promise<NotificationMarkReadResponse> => {
    const yourWorkTarget = yourWorkMarkReadTarget(id);
    const canMarkRead =
      !yourWorkTarget || isMarkableYourWorkSource(yourWorkTarget.source);
    const previous = count.value;

    if (yourWorkTarget !== null && !canMarkRead) {
      return {
        data: {
          id,
          isRead: false,
          readAt: null,
          unreadCount: count.value,
        },
      };
    }

    if (canMarkRead) {
      count.value = Math.max(0, count.value - 1);
    }

    try {
      if (yourWorkTarget !== null) {
        const marked = await markYourWorkRead(
          yourWorkTarget.source,
          yourWorkTarget.id,
        );
        const unreadCount = await fetchCount({ force: true });
        return {
          data: {
            id,
            isRead: marked,
            readAt: marked ? new Date().toISOString() : null,
            unreadCount,
          },
        };
      }

      const resp = await $fetch<NotificationMarkReadResponse>(
        `/api/me/notifications/${id}/read`,
        { method: 'POST', credentials: 'include' },
      );
      const unreadCount = await fetchCount({ force: true });
      return {
        ...resp,
        data: {
          ...resp.data,
          unreadCount,
        },
      };
    } catch (e) {
      count.value = previous;
      throw e;
    }
  };

  const markAllRead = async (): Promise<NotificationReadAllResponse> => {
    const previous = count.value;
    count.value = 0;

    try {
      const resp = await $fetch<NotificationReadAllResponse>(
        '/api/me/notifications/read-all',
        {
          method: 'POST',
          credentials: 'include',
        },
      );

      const unreadCount = await fetchCount({ force: true });
      return {
        data: {
          updated: resp?.data?.updated ?? 0,
          unreadCount,
        },
      };
    } catch (e) {
      count.value = previous;
      throw e;
    }
  };

  const deleteNotification = async (
    id: string,
  ): Promise<NotificationDeleteResponse> => {
    const resp = await $fetch<NotificationDeleteResponse>(
      `/api/me/notifications/${encodeURIComponent(id)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    const unreadCount = await fetchCount({ force: true });

    return {
      ...resp,
      data: {
        ...resp.data,
        unreadCount,
      },
    };
  };

  const incrementCount = (
    _sourceFeature?: string | null,
    amount = 1,
  ): number => {
    const delta = Number.isFinite(amount) ? Math.trunc(amount) : 1;
    count.value = Math.max(0, count.value + delta);
    return count.value;
  };

  return {
    count,
    list,
    fetchCount,
    markRead,
    markAllRead,
    deleteNotification,
    incrementCount,
  };
};

export type { NotificationRow };
